import { useState } from 'react';
import { useLocation } from 'react-router';
import { api } from '../../api/client';
import { useTenant } from '../../components/Shell';
import { Button, Card, Chip, Notice, PageHeader } from '../../components/ui';
import { Icon, type IconName } from '../../lib/icons';
import { useData } from '../../lib/useData';

const FEATURES: { icon: IconName; title: string; text: string }[] = [
  {
    icon: 'phone',
    title: 'Your numbers in Teams',
    text: 'Make and take calls in the Teams app on any computer or phone, with each person’s own number and your company’s caller ID. Dial colleagues by extension, just like from a desk phone.'
  },
  {
    icon: 'bell',
    title: 'Ring everywhere, answer anywhere',
    text: 'Choose for each person where their calls ring: their desk phone, Teams, or both at once. Whichever they pick up first takes the call.'
  },
  {
    icon: 'flow',
    title: 'Your call routing, unchanged',
    text: 'Auto attendants, ring groups, time of day rules and holidays work exactly as they do today. Teams is simply one more place calls can ring.'
  },
  {
    icon: 'headset',
    title: 'Call queues in Teams',
    text: 'Agents can take queue calls in Teams. Sign them in and out from Stitch or the wallboard, and queue reports count their calls the same way.'
  },
  {
    icon: 'eye',
    title: 'Who’s busy, in both places',
    text: 'When someone is on a Teams call, Stitch, the wallboard and colleagues’ busy lamps show them busy, and calls go to their voicemail (or wherever your company sends unanswered calls) instead of interrupting.'
  },
  {
    icon: 'key',
    title: 'Set up for you, in Stitch',
    text: 'No PowerShell and no separate phone system admin. Stitch connects to your Microsoft 365, matches people, assigns their numbers, and checks it all works.'
  }
];

const STEPS: { title: string; text: string }[] = [
  { title: 'Connect Microsoft 365', text: 'A Microsoft 365 admin signs in once from Stitch and approves the connection. Stitch never sees their password.' },
  { title: 'Add your Stitch address to Microsoft 365', text: 'Stitch gives you the address to add as a domain, and shows the record Microsoft asks for to prove it’s yours.' },
  { title: 'Check licences', text: 'Stitch lists who already has a Teams Phone licence and who still needs one.' },
  { title: 'Match people', text: 'Each person is matched to their Microsoft 365 account by email. Anyone who doesn’t match is shown so you can fix it.' },
  { title: 'Choose who uses Teams', text: 'Pick people, and where their calls should ring. Their own number comes with them.' },
  { title: 'Review, apply, test', text: 'Like every change in Stitch: you see the plan first, apply it, and can undo it. Then a quick test call to check each person is ready.' }
];

const NEEDS = [
  'A Microsoft 365 admin (Global administrator or Teams administrator) for about 15 minutes.',
  'A Microsoft Teams Phone licence for each person who will call from Teams: included in Microsoft 365 E5, and an add-on for plans such as Business Standard, Teams Essentials and E3. Stitch checks this for you.',
  'Each person’s email in Stitch matching their Microsoft 365 sign-in.',
  'Nothing to install: no extra servers, apps or phone system changes on your side.'
];

/** Microsoft Teams: coming soon, outlined in full so customers know what's coming and can ask for it. */
export function TenantTeams() {
  const { tenant } = useTenant();
  const { pathname } = useLocation();
  const people = useData(() => api.people(tenant.domain).catch(() => null), [tenant.domain]);
  const [sent, setSent] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');

  const list = people.data?.people ?? [];
  const withEmail = list.filter((p) => p.email).length;

  async function interested() {
    setSent('sending');
    try {
      await api.askSupport({
        message: `${tenant.name} would like Microsoft Teams calling as soon as it's ready (${list.length} people, ${withEmail} with an email address).`,
        page: pathname,
        tenant: tenant.domain
      });
      setSent('sent');
    } catch {
      setSent('failed');
    }
  }

  return (
    <>
      <PageHeader eyebrow="Integrations" title="Microsoft Teams" chips={<Chip tone="brand">Coming soon</Chip>} />
      <div className="content teams-page">
        <section className="teams-hero">
          <span className="teams-mark" aria-hidden="true">
            <Icon name="chat" size={30} strokeWidth={1.6} />
          </span>
          <div className="grow">
            <h2>Calls in Microsoft Teams, with your phone system behind them</h2>
            <p>
              Give people your company’s phone numbers in Teams. Calls reach them in Teams, on their desk phone, or both, and everything you’ve set up in Stitch keeps
              working the same way.
            </p>
            <div className="teams-cta">
              {sent === 'sent' ? (
                <Notice tone="ok">Thanks. Stitch support knows {tenant.name} wants Teams, and will be in touch when it’s ready for you.</Notice>
              ) : (
                <>
                  <Button variant="primary" icon="send" disabled={sent === 'sending'} onClick={() => void interested()}>
                    {sent === 'sending' ? 'Sending…' : 'Tell Stitch we’re interested'}
                  </Button>
                  <span className="muted small">We’ll let you know when it’s ready, and help you set it up.</span>
                </>
              )}
              {sent === 'failed' && <Notice tone="crit">That didn’t send. Try again, or use the Help button.</Notice>}
            </div>
          </div>
        </section>

        <div className="teams-grid">
          {FEATURES.map((f) => (
            <article key={f.title} className="teams-feature">
              <span className="teams-icon" aria-hidden="true">
                <Icon name={f.icon} size={18} />
              </span>
              <h3>{f.title}</h3>
              <p className="muted">{f.text}</p>
            </article>
          ))}
        </div>

        <div className="teams-cols">
          <Card title="How setup will work">
            <ol className="teams-steps">
              {STEPS.map((s, i) => (
                <li key={s.title}>
                  <span className="teams-step-n" aria-hidden="true">
                    {i + 1}
                  </span>
                  <span className="cell-stack">
                    <strong>{s.title}</strong>
                    <span className="muted">{s.text}</span>
                  </span>
                </li>
              ))}
            </ol>
          </Card>
          <div className="teams-side">
            <Card title="What you’ll need">
              <ul className="teams-needs">
                {NEEDS.map((n) => (
                  <li key={n}>
                    <Icon name="check" size={15} />
                    <span>{n}</span>
                  </li>
                ))}
              </ul>
            </Card>
            {people.data && list.length > 0 && (
              <Card title={`${tenant.name} today`}>
                <div className="card-body teams-ready">
                  <p>
                    <strong className="num">{withEmail}</strong> of <strong className="num">{list.length}</strong> people have an email address in Stitch, which is how
                    they’ll be matched to Microsoft 365.
                  </p>
                  {withEmail < list.length && <p className="muted small">Add the missing ones on People whenever you like; nothing else is needed yet.</p>}
                </div>
              </Card>
            )}
          </div>
        </div>
        <p className="muted small">
          Microsoft and Microsoft Teams are trademarks of the Microsoft group of companies. Licences are bought from Microsoft or your Microsoft 365 provider.
        </p>
      </div>
    </>
  );
}
